import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CloseAllTradesModal } from '@pages/Trade/Portfolio/Modals/CloseAllTradesModal'
import { CloseTradeModal } from '@pages/Trade/Portfolio/Modals/CloseTradeModal'
import { TradeDetailsOpenModal } from '@pages/Trade/Portfolio/Modals/TradeDetailsOpenModal'
import { getStepDecimals } from '@pages/Trade/utils/helpers'

import { useModal } from '@context/ModalsContext'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Body1, Caption1, Caption2 } from '@ui/Typography'
import { Badge } from '@ui/components/Badge/Badge'
import { IconButton } from '@ui/components/Button'
import { CloseIcon } from '@ui/icons/Close.icon'

import { CurrencyType } from '@model/WalletDto'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { Box } from '@shared/components/Box/Box'

import { useFixTradingWebSocketContext } from '../context/FixTradingWebSocketContext'
import type { DxtradeInstrumentMap } from '../hooks/useDxtradeInstruments'
import { formatSymbol, renderCellValue, renderSide } from '../utils/formatters'
import type { CardTradingGroup } from './Card-trading'
import { LivePriceCell } from './LivePriceCell'
import { TradeCardList } from './TradeCardList'
import { TradeCardRow } from './TradeCardRow'
import { GroupPnL as CardGroupPnL, TradePnL as CardTradePnL } from './TradePnL'

import styles from './CardTradingView.module.scss'

const sx: Record<string, string> = styles as unknown as Record<string, string>

interface Props {
  groups: CardTradingGroup[]
  isLoading: boolean
  accountCurrency?: CurrencyType
  accountCode?: string
  singleTradeLabelMode?: 'symbol' | 'side'
  dxtradeInstruments?: DxtradeInstrumentMap
  hasActiveFilters?: boolean
}

export const CardTradingView: React.FC<Props> = ({
  groups,
  isLoading,
  accountCurrency,
  accountCode,
  singleTradeLabelMode = 'symbol',
  dxtradeInstruments,
  hasActiveFilters,
}) => {
  const { t } = useTranslation()
  const { formatNumber } = useFormatNumber()
  const tradeDetailsOpenModal = useModal(TradeDetailsOpenModal)
  const { newOrder, replaceOrder, closePosition, closeAllPositions, cancelOrder } =
    useFixTradingWebSocketContext()
  const closeTradeModal = useModal(CloseTradeModal, { closePosition })
  const closeAllTradesModal = useModal(CloseAllTradesModal, { closeAllPositions })

  const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {}
    groups.forEach((group) => {
      initial[group.symbol] = false
    })
    return initial
  })

  useEffect(() => {
    setExpandedMap((prev) => {
      const next: Record<string, boolean> = {}
      groups.forEach((group) => {
        next[group.symbol] = prev[group.symbol] ?? false
      })
      return next
    })
  }, [groups])

  const isEmpty = groups.length === 0

  return (
    <Box flex direction='column' gap='x12' mt={isEmpty ? undefined : 'x4'}>
      <TradeCardList isLoading={isLoading} isEmpty={isEmpty} hasActiveFilters={hasActiveFilters}>
        {groups.map((group) => {
          const isSingle = group.trades.length === 1
          const trade = isSingle ? group.trades[0] : undefined

          const fractionDigits = getStepDecimals(trade?.priceIncrement)

          if (isSingle) {
            const sideLabel =
              singleTradeLabelMode === 'side'
                ? renderSide(trade!.side, Body1)
                : formatSymbol(group.symbol, trade!.description)

            return (
              <TradeCardRow
                key={trade!.tradeId}
                sideLabel={sideLabel}
                rightPrimary={<CardTradePnL trade={trade!} accountCurrency={accountCurrency} />}
                onClick={() => {
                  const instrument = dxtradeInstruments?.[trade!.symbol || '']
                  if (!instrument || instrument.priceIncrement == null) {
                    return
                  }
                  tradeDetailsOpenModal.open({
                    trade: trade!,
                    accountCurrency,
                    newOrder,
                    closePosition,
                    cancelOrder,
                    replaceOrder,
                    accountCode,
                    instrumentMeta: {
                      pipSize: instrument.pipSize,
                      priceIncrement: instrument.priceIncrement,
                    },
                  })
                }}
                caption={
                  <Box flex justify='space-between' align='center'>
                    <Box flex gap='x4'>
                      {singleTradeLabelMode === 'symbol' && renderSide(trade!.side)}
                      <Caption1 color='text-med-em' sensitive>{`${
                        trade?.sizeLots ? trade.sizeLots : ''
                      } ${t('at')} ${
                        formatNumber(trade?.entryPrice, {
                          minDecimalPlaces: fractionDigits,
                          maxDecimalPlaces: fractionDigits,
                        }) ?? '-'
                      }`}</Caption1>
                    </Box>

                    <LivePriceCell
                      trade={{
                        symbol: trade!.symbol,
                        side: trade!.side,
                        priceIncrement: trade?.priceIncrement,
                      }}
                      TypographyComp={Caption1}
                      color='text-med-em'
                      sensitive
                    />
                  </Box>
                }
                meta={
                  <Box flex gap='x8' mt='x4'>
                    {trade!.stopLoss != null && (
                      <Badge type='black' size='xxs' contrast='low'>
                        <Caption2 color='text-med-em'>SL</Caption2>
                        &nbsp;
                        <Caption2 color='text-med-em'>
                          <SensitiveData>
                            {formatNumber(trade!.stopLoss, {
                              minDecimalPlaces: fractionDigits,
                              maxDecimalPlaces: fractionDigits,
                            }) ?? '-'}
                          </SensitiveData>
                        </Caption2>
                      </Badge>
                    )}
                    {trade!.takeProfit != null && (
                      <Badge type='black' size='xxs' contrast='low'>
                        <Caption2 color='text-med-em'>TP</Caption2>
                        &nbsp;
                        <Caption2 color='text-med-em'>
                          <SensitiveData>
                            {formatNumber(trade!.takeProfit, {
                              minDecimalPlaces: fractionDigits,
                              maxDecimalPlaces: fractionDigits,
                            }) ?? '-'}
                          </SensitiveData>
                        </Caption2>
                      </Badge>
                    )}
                  </Box>
                }
                className={sx.singleTradeRow}
                onClose={() => {
                  closeTradeModal.open({
                    trade: trade!,
                    accountCurrency,
                    accountCode,
                  })
                }}
              />
            )
          }

          return (
            <Box key={group.symbol} flex direction='column' gap='x12' className={sx.group}>
              <Box
                flex
                justify='space-between'
                align='center'
                px='x16'
                py='x12'
                radius='x16'
                gap='x12'
                className={[styles.outlined, sx.groupHeader].join(' ')}
                role='button'
                tabIndex={0}
                aria-expanded={!!expandedMap[group.symbol]}
                onClick={() =>
                  setExpandedMap((prev) => ({
                    ...prev,
                    [group.symbol]: !prev[group.symbol],
                  }))
                }
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setExpandedMap((prev) => ({
                      ...prev,
                      [group.symbol]: !prev[group.symbol],
                    }))
                  }
                }}
              >
                <Box flex justify='space-between' className={styles.flexGrowBox}>
                  <Box flex direction='column'>
                    <Box flex align='center' gap='x6'>
                      <Body1 medium>
                        {formatSymbol(group.symbol, group.trades[0].description)}
                      </Body1>
                      <Badge type='purple' size='xs' contrast='low'>
                        {group.trades.length}
                      </Badge>
                    </Box>
                    <Caption1 color='text-med-em'>
                      <SensitiveData>
                        {typeof group.sizeLots === 'number'
                          ? group.sizeLots.toFixed(2)
                          : renderCellValue(group.sizeLots)}
                      </SensitiveData>
                    </Caption1>
                  </Box>
                  <Box flex direction='column' align='flex-end'>
                    <CardGroupPnL trades={group.trades} accountCurrency={accountCurrency} />
                  </Box>
                </Box>
                <IconButton.Ghost
                  aria-label={'Close all trades'}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation()
                    closeAllTradesModal.open({
                      trades: group.trades,
                      accountCurrency,
                      accountCode: accountCode ?? '',
                      assetSymbol: group.symbol,
                    })
                  }}
                  icon={{ icon: CloseIcon, color: 'text-med-em' }}
                />
              </Box>
              <Box
                className={[
                  sx.childrenCollapse,
                  expandedMap[group.symbol] ? sx.expanded : sx.collapsed,
                ].join(' ')}
                aria-hidden={!expandedMap[group.symbol]}
              >
                <Box flex direction='column' gap='x12' className={sx.childrenList}>
                  {group.trades.map((trade) => {
                    const fractionDigits = getStepDecimals(trade.priceIncrement)

                    return (
                      <TradeCardRow
                        key={trade.tradeId}
                        onClick={() => {
                          const instrument = dxtradeInstruments?.[trade.symbol || '']
                          if (!instrument || instrument.priceIncrement == null) {
                            return
                          }
                          tradeDetailsOpenModal.open({
                            trade: trade!,
                            accountCurrency,
                            newOrder,
                            closePosition,
                            cancelOrder,
                            accountCode,
                            instrumentMeta: {
                              pipSize: instrument.pipSize,
                              priceIncrement: instrument.priceIncrement,
                            },
                          })
                        }}
                        sideLabel={renderSide(trade.side, Body1)}
                        rightPrimary={
                          <CardTradePnL trade={trade} accountCurrency={accountCurrency} />
                        }
                        caption={
                          <>
                            <SensitiveData>{trade?.sizeLots ?? ''}</SensitiveData> {t('at')}{' '}
                            <SensitiveData>
                              {formatNumber(trade?.entryPrice, {
                                minDecimalPlaces: fractionDigits,
                                maxDecimalPlaces: fractionDigits,
                              }) ?? '-'}
                            </SensitiveData>
                          </>
                        }
                        meta={
                          <Box flex gap='x8' mt='x4'>
                            {trade.stopLoss != null && (
                              <Badge type='black' size='xxs' contrast='low'>
                                <Caption2 color='text-med-em'>SL</Caption2>
                                &nbsp;
                                <Caption2 color='text-med-em'>
                                  {formatNumber(trade.stopLoss, {
                                    minDecimalPlaces: fractionDigits,
                                    maxDecimalPlaces: fractionDigits,
                                  }) ?? '-'}
                                </Caption2>
                              </Badge>
                            )}
                            {trade.takeProfit != null && (
                              <Badge type='black' size='xxs' contrast='low'>
                                <Caption2 color='text-med-em'>TP</Caption2>
                                &nbsp;
                                <Caption2 color='text-med-em'>
                                  {formatNumber(trade.takeProfit, {
                                    minDecimalPlaces: fractionDigits,
                                    maxDecimalPlaces: fractionDigits,
                                  }) ?? '-'}
                                </Caption2>
                              </Badge>
                            )}
                          </Box>
                        }
                        onClose={() => {
                          closeTradeModal.open({
                            trade,
                            accountCurrency,
                            accountCode,
                          })
                        }}
                      />
                    )
                  })}
                </Box>
              </Box>
            </Box>
          )
        })}
      </TradeCardList>
    </Box>
  )
}

export default CardTradingView
